import { useEffect, useMemo, useRef, useState } from "react";

/**
 * MedAutocomplete
 * Custom autocomplete replacing native <datalist> so the medicine name
 * is always clearly visible while typing.
 *
 * Props:
 *  - value: string
 *  - onChange: (val: string) => void  (called on every keystroke)
 *  - onPick?: (inventoryRow) => void  (called when a suggestion is chosen)
 *  - options: Array<{ id, name, sku, selling_price, stock_qty }>
 *  - testId: string
 *  - placeholder?: string
 */
export default function MedAutocomplete({
  value,
  onChange,
  onPick,
  options = [],
  testId,
  placeholder = "Medicine (type to search stock)",
}) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const wrapRef = useRef(null);

  const q = (value || "").trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return options.slice(0, 30);
    return options
      .filter(
        (m) =>
          (m.name || "").toLowerCase().includes(q) ||
          (m.sku || "").toLowerCase().includes(q)
      )
      .slice(0, 30);
  }, [q, options]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (m) => {
    onChange(m.name);
    onPick && onPick(m);
    setOpen(false);
    setHighlight(-1);
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      if (open && highlight >= 0 && filtered[highlight]) {
        e.preventDefault();
        pick(filtered[highlight]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const emptyStock = options.length === 0;

  return (
    <div ref={wrapRef} className="relative min-w-0">
      <input
        data-testid={testId}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setHighlight(-1);
        }}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        autoComplete="off"
        className="w-full h-10 px-3 rounded-lg bg-white border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 min-w-0 text-[#2A2624]"
      />
      {open && (
        <div
          data-testid={`${testId}-dropdown`}
          className="absolute left-0 right-0 top-full mt-1 z-50 max-h-64 overflow-y-auto bg-white border border-sand rounded-lg shadow-lg"
        >
          {emptyStock ? (
            <div className="px-3 py-3 text-xs text-[#5C5652]" data-testid={`${testId}-empty-stock`}>
              <div className="font-semibold text-[#C25946] mb-1">No stock loaded for this branch</div>
              Ask the manager to add inventory (Manager · Stock) so medicines auto-suggest here.
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-3 py-3 text-xs text-[#5C5652]" data-testid={`${testId}-no-match`}>
              No stock matches &quot;{value}&quot;. You can still type the name manually.
            </div>
          ) : (
            filtered.map((m, idx) => (
              <button
                type="button"
                key={m.id || `${m.sku}-${idx}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(m);
                }}
                onMouseEnter={() => setHighlight(idx)}
                className={`w-full text-left px-3 py-2 border-b border-sand/60 last:border-b-0 ${
                  highlight === idx ? "bg-[#F0EBDE]" : "bg-white hover:bg-[#F9F6F0]"
                }`}
              >
                <div className="font-semibold text-[#2A2624] text-sm leading-tight">
                  {m.name}
                </div>
                <div className="text-[11px] text-[#5C5652] mt-0.5">
                  {m.sku} · ₹ {m.selling_price} ·{" "}
                  <span
                    className={
                      (m.stock_qty ?? 0) <= 0
                        ? "text-red-600 font-semibold"
                        : "text-[#5C5652]"
                    }
                  >
                    stock {m.stock_qty ?? 0}
                  </span>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
